import React, { useState } from 'react';
import { Plus, Download, ShieldCheck } from 'lucide-react';
import { ErrorRule } from '../../../../types';
import { validateRuleSet, IMPLICIT_DEFAULTS } from '../../../../utils/errorRuleset';
import { ERROR_RULESET_PRESETS } from '../../../../constants/errorRulesetPresets';
import { RuleCard } from './RuleCard';

interface RuleEditorProps {
  rules: ErrorRule[];
  onChange: (rules: ErrorRule[]) => void;
  /** Optional header (ruleset name + usage) shown above the list. */
  heading?: React.ReactNode;
}

const NEW_RULE: ErrorRule = { match: {}, action: { disposition: 'throw', errorCode: '' } };

export const RuleEditor: React.FC<RuleEditorProps> = ({ rules, onChange, heading }) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [showPresets, setShowPresets] = useState(false);
  const issues = validateRuleSet(rules);

  const updateRule = (i: number, rule: ErrorRule) => onChange(rules.map((r, idx) => idx === i ? rule : r));
  const moveRule = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const duplicateRule = (i: number) => {
    const next = [...rules];
    next.splice(i + 1, 0, JSON.parse(JSON.stringify(rules[i])));
    onChange(next);
  };
  const deleteRule = (i: number) => onChange(rules.filter((_, idx) => idx !== i));
  const addRule = () => onChange([...rules, JSON.parse(JSON.stringify(NEW_RULE))]);
  const toggleCollapse = (i: number) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">{heading}</div>
        <div className="relative shrink-0">
          <button type="button" onClick={() => setShowPresets(s => !s)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:border-teal-400">
            <Download size={14} /> From preset ▾
          </button>
          {showPresets && (
            <div className="absolute right-0 mt-1 z-10 w-64 max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-1">
              {ERROR_RULESET_PRESETS.map(p => (
                <button key={p.key} type="button"
                  onClick={() => { onChange(JSON.parse(JSON.stringify(p.rules))); setShowPresets(false); }}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800">
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{p.name}</div>
                  <div className="text-[10px] text-slate-400 leading-tight">{p.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-400">Rules evaluated top-to-bottom; first match wins.</p>

      <div className="space-y-2">
        {rules.map((rule, i) => (
          <RuleCard
            key={i}
            rule={rule}
            index={i}
            total={rules.length}
            collapsed={collapsed.has(i)}
            issues={issues.filter(iss => iss.ruleIndex === i)}
            onChange={(r) => updateRule(i, r)}
            onMove={(dir) => moveRule(i, dir)}
            onDuplicate={() => duplicateRule(i)}
            onDelete={() => deleteRule(i)}
            onToggleCollapse={() => toggleCollapse(i)}
          />
        ))}
        {rules.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-4 text-center text-xs text-slate-400">
            No custom rules. This task uses the default behaviour: transient errors retry forever, everything else faults.
          </div>
        )}
      </div>

      <button type="button" onClick={addRule}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-dashed border-slate-300 dark:border-slate-700 text-slate-600 hover:border-teal-400 hover:text-teal-600 w-full justify-center">
        <Plus size={16} /> Add rule
      </button>

      {/* read-only implicit defaults footer */}
      <div className="rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
          <ShieldCheck size={12} /> Defaults (always applied last)
        </div>
        <ul className="mt-1 text-[11px] text-slate-500 space-y-0.5">
          {IMPLICIT_DEFAULTS.map((d, i) => <li key={i}>• {d}</li>)}
        </ul>
      </div>
    </div>
  );
};
